<template>
  <div>
    <h2
      v-if="sectionTitle"
      class="text-h3 text-left font-semibold leading-7 mb-8"
    >
      {{ sectionTitle }}
    </h2>

    <HorizontalScrollWrapper
      :items-length="cityCategoriesCardList.length"
      class="gap-4 mb-5"
    >
      <NuxtLinkLocale
        v-for="item in cityCategoriesCardList"
        :key="item.title"
        :to="`${item.link}`"
        class="group transition-all hover:scale-[103%]"
      >
        <TransactionalPageCardListItem v-bind="item" />
      </NuxtLinkLocale>
    </HorizontalScrollWrapper>
  </div>
</template>

<script setup lang="ts">
import * as qs from 'qs'
import type { LocalityCategory } from '~/composables/types/locality-category'
import type { Category } from '~/composables/types/event'
import type { KftApiDefaultResponse } from '~/composables/types/kft-api'
import {
  eventListPageCategoryUrl,
  eventListPageLocalityCategoriesUrl,
} from '~/components/transactionalPage/requestParams'

const { sectionTitle, citySlug, categorySlug, subtitle } = defineProps<{
  sectionTitle?: string
  citySlug: string
  subtitle: string
  categorySlug: string
}>()

const cityCategoriesCardList = useState<
  {
    title: string
    subtitle: string
    imageUrl: string | undefined
    link: string
  }[]
>(`transactional-page-city-categories-list-${citySlug}`, () => [])

const fullLocalizedUrl = useFullLocalizedUrl()

type TransformedLocalityCategory = {
  permalink: string
  title: string
}

const localityCategoriesPromise = useKftApiFetch<
  KftApiDefaultResponse<LocalityCategory[]>,
  TransformedLocalityCategory[]
>(eventListPageLocalityCategoriesUrl(), {
  transform: (res) => {
    // Transform is useful for eliminating large amounts of unnecessary data
    // that will occupy space inside the document (ssr)
    return (
      res?.data?.map((category) => ({
        title: category.title,
        permalink: category.permalink,
      })) || ([] as TransformedLocalityCategory[])
    )
  },
})

const categoriesPromise = useKftApiFetch<KftApiDefaultResponse<Category>>(
  eventListPageCategoryUrl({ categorySlug })
)

const [{ data: categoriesResponse }, { data: localityCategoriesResponse }] =
  await Promise.all([categoriesPromise, localityCategoriesPromise])

const localityCategories = localityCategoriesResponse?.value || []
const categories = computed(() => categoriesResponse?.value?.data || [])

const fnMountCityPageHorizontalList = () => {
  const categoriesObj: Record<string, string> = {}
  if (!localityCategories.length) {
    return
  }

  localityCategories
    ?.filter((category) => category.permalink.split('/')[0] === citySlug)
    .forEach((category) => {
      const idx = category.permalink.split('/')[1]
      categoriesObj[idx] = category.title
    })

  cityCategoriesCardList.value = Object.keys(categoriesObj).map((slug) => {
    const catgoryDataObj = categories.value.find(
      (category) => category.slug === slug
    )

    return {
      title: categoriesObj[slug],
      subtitle,
      imageUrl: catgoryDataObj
        ? categoryGetters.getThumbnail(catgoryDataObj)
        : undefined,
      link: fullLocalizedUrl(`/${citySlug}/${slug}`),
    }
  })
}

if (cityCategoriesCardList.value.length === 0) {
  fnMountCityPageHorizontalList()
}
</script>
